/*
 * Created on Wed Nov 01 2023 4:27:51 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
import React, { useState, useEffect } from "react";
import { ArrowRight, Sparkles, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getIcon } from "../utils/iconResolver";
import data from "../data/krumagoData.json";

/* ─────────────────────────────────────────────────────────────
   WaaS product card — injected alongside existing industries
   ───────────────────────────────────────────────────────────── */
const WAAS_CARD = {
  label: "Krumago WaaS",
  description: "API-first platform to provision & deploy static business websites at scale",
  icon: "Globe",                          // resolved via getIcon
  gradient: "from-indigo-600 to-violet-600",
  href: "/waas-management-software",
  badge: "NEW",
};

const ParentHero = () => {
  const { hero } = data;
  const [currentSlide, setCurrentSlide] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % hero.carouselImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [hero.carouselImages.length]);

  const scrollToPricing = () => {
    document.getElementById("parent-industries")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleButtonClick = (btn) => {
    if (btn.scrollTo) {
      scrollToPricing();
    } else if (btn.href) {
      nav(btn.href);
    }
  };

  /* Merge WaaS into existing industry list */
  const industries = [...(hero.industries || []), WAAS_CARD];

  return (
    <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden bg-slate-950">
      {/* ── Background Carousel ── */}
      <div className="absolute inset-0 overflow-hidden">
        {hero.carouselImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
            style={{ backgroundImage: `url(${image})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-slate-950/98 via-slate-900/96 to-slate-950/98" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/80" />
          </div>
        ))}
      </div>

      {/* ── Carousel Dots ── */}
      <div className="absolute bottom-8 sm:bottom-12 lg:bottom-20 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10">
        {hero.carouselImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentSlide ? "bg-white scale-125" : "bg-white/40 hover:bg-white/60"
            }`}
          />
        ))}
      </div>

      {/* ── Animated Glows ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-72 h-72 sm:w-[500px] sm:h-[500px] bg-blue-500/5 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-1/4 -right-20 w-80 h-80 sm:w-[600px] sm:h-[600px] bg-indigo-500/5 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1500ms" }}
        />
      </div>

      {/* ── Floating Particles ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/10 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${15 + Math.random() * 10}s`,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full py-20 sm:py-24 mt-14 sm:mt-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">

          {/* ── LEFT — Headline ── */}
          <div className="text-white space-y-6 sm:space-y-8">
            <div className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-xl border border-white/10 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm font-semibold">
              <Sparkles className="w-4 h-4 text-slate-300 flex-shrink-0" />
              <span className="text-slate-200">{hero.badge}</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight leading-none">
              <span className="block text-white mb-2 sm:mb-3">{hero.headline.line1}</span>
              <span className="block mb-2 sm:mb-3 font-serif font-black drop-shadow-[0_0_30px_rgba(255,255,255,0.4)]">
                <span className="text-[rgb(0,0,168)]">{hero.headline.brand.part1}</span>
                <span className="text-[rgb(227,6,19)]">{hero.headline.brand.part2}</span>
                <span className="text-[rgb(0,0,168)]">{hero.headline.brand.part3}</span>
              </span>
              <span className="block text-white">{hero.headline.line3}</span>
            </h1>

            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl font-normal">
              {hero.description}
            </p>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              {hero.buttons.map((btn, index) => (
                index === 0 ? (
                  <button
                    key={index}
                    onClick={() => handleButtonClick(btn)}
                    className="group relative inline-flex items-center justify-center gap-3 px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl overflow-hidden text-sm sm:text-base w-full sm:w-auto"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700 ease-out" />
                    <Sparkles className="w-4 h-4" />
                    <span>{btn.label}</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                ) : (
                  <button
                    key={index}
                    onClick={() => handleButtonClick(btn)}
                    className="inline-flex items-center justify-center gap-3 px-6 sm:px-8 py-3.5 sm:py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-xl hover:bg-white/20 transition-all duration-300 text-sm sm:text-base w-full sm:w-auto"
                  >
                    {btn.label}
                  </button>
                )
              ))}
            </div>
          </div>

          {/* ── RIGHT — Industry / Product Cards ── */}
          <div className="space-y-3 sm:space-y-4">
            {industries.map((industry, index) => {
              const isWaaS = industry.label === WAAS_CARD.label;
              return (
                <button
                  key={index}
                  onClick={() => nav(industry.href)}
                  className={`group w-full backdrop-blur-xl border rounded-2xl p-4 sm:p-6 flex items-center justify-between transition-all duration-400 text-left ${
                    isWaaS
                      ? "bg-indigo-950/40 border-indigo-500/30 hover:border-indigo-400/60 hover:bg-indigo-900/40"
                      : "bg-white/5 border-white/10 hover:border-white/25 hover:bg-white/10"
                  }`}
                >
                  <div className="flex items-center gap-4 sm:gap-5">
                    <div
                      className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${industry.gradient} rounded-2xl flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 group-hover:rotate-3 transition-all duration-400 shadow-lg`}
                    >
                      {getIcon(industry.icon, { className: "w-5 h-5 sm:w-6 sm:h-6" })}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="text-white font-black text-lg sm:text-xl">{industry.label}</div>
                        {isWaaS && (
                          <span className="text-[10px] font-black tracking-wider px-2 py-0.5 rounded-full bg-indigo-500/30 text-indigo-300 border border-indigo-400/30 uppercase">
                            NEW
                          </span>
                        )}
                      </div>
                      <div className="text-slate-400 text-xs sm:text-sm mt-0.5">{industry.description}</div>
                    </div>
                  </div>
                  <ChevronRight className={`w-5 h-5 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0 ${
                    isWaaS ? "text-indigo-400/60 group-hover:text-indigo-300" : "text-slate-500 group-hover:text-white"
                  }`} />
                </button>
              );
            })}
          </div>

        </div>
      </div>
    </section>
  );
};

export default ParentHero;
